// src/components/LotteryPickerWithAutofill.jsx
import React, { useEffect, useMemo, useState } from "react";
import { LOTTERIES } from "../Objects/Lotteries";

/**
 * Lottery picker with autofill and live validation.
 *
 * Usage: <LotteryPickerWithAutofill />
 */

// Utility: draw `count` unique random integers in [min..max]
function drawUniqueRandom(count, min, max) {
  const set = new Set();
  const range = max - min + 1;
  if (count > range) throw new Error("Not enough numbers in range");
  while (set.size < count) {
    const n = Math.floor(Math.random() * range) + min;
    set.add(n);
  }
  return Array.from(set);
}

// Normalize user input -> integer or NaN
const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : NaN;
};

export default function LotteryPickerWithAutofill() {
  const [selected, setSelected] = useState("Powerball");
  const config = LOTTERIES[selected];

  // user picks stored as strings (so partial typing is allowed)
  const [mainInputs, setMainInputs] = useState(
    Array(config.mainCount).fill("")
  );
  const [bonusInput, setBonusInput] = useState("");

  // draw result
  const [drawnMain, setDrawnMain] = useState(null);
  const [drawnBonus, setDrawnBonus] = useState(null);

  // validation state
  const [errors, setErrors] = useState([]); // array of string errors
  const [fieldErrors, setFieldErrors] = useState({
    main: Array(config.mainCount).fill(null),
    bonus: null,
  });

  // When lottery changes, reset inputs/validation/draw
  useEffect(() => {
    const conf = LOTTERIES[selected];
    setMainInputs(Array(conf.mainCount).fill(""));
    setBonusInput("");
    setDrawnMain(null);
    setDrawnBonus(null);
    setErrors([]);
    setFieldErrors({
      main: Array(conf.mainCount).fill(null),
      bonus: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Live validation: runs whenever inputs or config change
  useEffect(() => {
    const mainCount = config.mainCount;
    const mainMin = config.mainMin;
    const mainMax = config.mainMax;
    const bonusMin = config.bonusMin;
    const bonusMax = config.bonusMax;

    const feMain = Array(mainCount).fill(null);
    let feBonus = null;
    const errs = [];

    // Parse and check main
    const parsedMain = mainInputs.map((s) => toNum(s));

    // range and required checks
    parsedMain.forEach((n, i) => {
      if (Number.isNaN(n)) {
        feMain[i] = "required";
      } else if (n < mainMin || n > mainMax) {
        feMain[i] = `range (${mainMin}-${mainMax})`;
      }
    });

    // duplicates among main
    const counts = {};
    parsedMain.forEach((n) => {
      if (!Number.isNaN(n)) counts[n] = (counts[n] || 0) + 1;
    });
    parsedMain.forEach((n, i) => {
      if (!Number.isNaN(n) && counts[n] > 1) {
        feMain[i] = "duplicate";
      }
    });

    // bonus checks
    const parsedBonus = toNum(bonusInput);
    if (Number.isNaN(parsedBonus)) {
      feBonus = "required";
    } else if (parsedBonus < bonusMin || parsedBonus > bonusMax) {
      feBonus = `range (${bonusMin}-${bonusMax})`;
    }

    // Compose human-readable error list
    if (feMain.some(Boolean)) {
      const perIndex = feMain
        .map((e, i) => (e ? `Main #${i + 1}: ${e}` : null))
        .filter(Boolean);
      errs.push(...perIndex);
    }
    if (feBonus) {
      errs.push(`${config.bonusName}: ${feBonus}`);
    }

    setFieldErrors({ main: feMain, bonus: feBonus });
    setErrors(errs);
  }, [mainInputs, bonusInput, config]);

  // Helper: suggest available numbers for a main slot (numbers in range not taken)
  const availableMainNumbers = useMemo(() => {
    const taken = new Set(
      mainInputs.map((s) => toNum(s)).filter((n) => !Number.isNaN(n))
    );
    const arr = [];
    for (let n = config.mainMin; n <= config.mainMax; n++) {
      if (!taken.has(n)) arr.push(n);
    }
    return arr;
  }, [mainInputs, config]);

  // Handlers
  const handleMainChange = (idx, raw) => {
    // Accept only digits and empty; remove leading zeros
    const cleaned = raw.replace(/^0+/, "") || "";
    const arr = [...mainInputs];
    arr[idx] = cleaned;
    // Auto-sort after all fields are filled and valid (optional)
    setMainInputs(arr);
  };

  const handleMainBlur = (idx) => {
    // On blur: convert to number if valid, otherwise leave as-is.
    const val = mainInputs[idx];
    const n = toNum(val);
    if (!Number.isNaN(n)) {
      // Optionally auto-sort the main numbers if all present & unique
      const parsed = mainInputs.map((s) => toNum(s));
      if (!parsed.some(Number.isNaN)) {
        // if all numbers are present -> sort them for neatness
        const sorted = [...parsed].sort((a, b) => a - b).map(String);
        setMainInputs(sorted);
      } else {
        // ensure stored value is trimmed number
        const arr = [...mainInputs];
        arr[idx] = String(n);
        setMainInputs(arr);
      }
    }
  };

  const handleBonusChange = (raw) => {
    const cleaned = raw.replace(/^0+/, "") || "";
    setBonusInput(cleaned);
  };

  const quickPick = () => {
    // Fill mains with unique randoms, sort them, fill bonus with random
    const main = drawUniqueRandom(config.mainCount, config.mainMin, config.mainMax)
      .sort((a, b) => a - b)
      .map(String);
    const bonus = String(
      Math.floor(Math.random() * (config.bonusMax - config.bonusMin + 1)) +
        config.bonusMin
    );
    setMainInputs(main);
    setBonusInput(bonus);
    setDrawnMain(null);
    setDrawnBonus(null);
  };

  const fillSuggested = (idx, value) => {
    const arr = [...mainInputs];
    arr[idx] = String(value);
    setMainInputs(arr);
  };

  // Draw (only called when inputs valid)
  const runDraw = () => {
    // final re-validate - rely on fieldErrors
    if (errors.length > 0) {
      // should be disabled, but guard anyway
      return;
    }

    // Parse user picks
    const userMainNums = mainInputs.map((s) => toNum(s));
    const userBonusNum = toNum(bonusInput);

    // draw numbers
    const drawn = drawUniqueRandom(config.mainCount, config.mainMin, config.mainMax).sort(
      (a, b) => a - b
    );
    const drawnB =
      Math.floor(Math.random() * (config.bonusMax - config.bonusMin + 1)) +
      config.bonusMin;

    setDrawnMain(drawn);
    setDrawnBonus(drawnB);

    const matchedMain = userMainNums.filter((n) => drawn.includes(n)).length;
    const matchedBonus = userBonusNum === drawnB;

    // small simple alert for result
    setTimeout(() => {
      alert(
        `Draw complete!\n${config.name} Draw: ${drawn.join(", ")} (${config.bonusName}: ${drawnB})\nYou matched ${matchedMain} main number(s) and ${matchedBonus ? "the bonus!" : "no bonus"}`
      );
    }, 50);
  };

  // UI helpers
  const fieldIsInvalid = (err) => Boolean(err);
  const canDraw = errors.length === 0;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Lottery Simulator — Pick & Validate</h1>

      <div style={styles.row}>
        <label style={styles.label}>Choose Lottery:</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          style={styles.select}
        >
          {Object.keys(LOTTERIES).map((k) => (
            <option key={k} value={k}>
              {LOTTERIES[k].name}
            </option>
          ))}
        </select>

        <button onClick={quickPick} style={styles.quickPick}>
          Quick Pick
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        <h3 style={{ marginBottom: 6 }}>Your Numbers — {config.name}</h3>

        <div style={styles.mainRow}>
          {mainInputs.map((val, idx) => {
            const ferr = fieldErrors.main[idx];
            return (
              <div key={idx} style={styles.inputBlock}>
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  type="text"
                  value={val}
                  onChange={(e) => handleMainChange(idx, e.target.value)}
                  onBlur={() => handleMainBlur(idx)}
                  placeholder={`#${idx + 1}`}
                  aria-label={`Main number ${idx + 1}`}
                  style={{
                    ...styles.numberInput,
                    borderColor: ferr ? "#e74c3c" : "#ccc",
                    background: ferr ? "#fff6f6" : "#fff",
                  }}
                />
                {ferr && <div style={styles.fieldError}>{ferr}</div>}

                {/* suggestion dropdown: show first 6 available numbers to click */}
                <div style={styles.suggestRow}>
                  {availableMainNumbers.slice(0, 6).map((n) => (
                    <button
                      key={n}
                      title="Fill this number"
                      onClick={() => fillSuggested(idx, n)}
                      style={styles.suggestBtn}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: "block", marginBottom: 6 }}>
            {config.bonusName} ( {config.bonusMin} - {config.bonusMax} )
          </label>
          <input
            inputMode="numeric"
            type="text"
            value={bonusInput}
            onChange={(e) => handleBonusChange(e.target.value)}
            style={{
              ...styles.bonusInput,
              borderColor: fieldIsInvalid(fieldErrors.bonus) ? "#e74c3c" : "#ccc",
              background: fieldIsInvalid(fieldErrors.bonus) ? "#fff6f6" : "#fff",
            }}
            placeholder={config.bonusName}
          />
          {fieldErrors.bonus && <div style={styles.fieldError}>{fieldErrors.bonus}</div>}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <strong>Validation:</strong>
        {errors.length === 0 ? (
          <span style={{ marginLeft: 8, color: "green" }}>All inputs valid ✅</span>
        ) : (
          <ul style={{ marginTop: 6 }}>
            {errors.map((e, i) => (
              <li key={i} style={{ color: "#e74c3c" }}>
                {e}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <button
          onClick={runDraw}
          disabled={!canDraw}
          style={{
            ...styles.drawBtn,
            background: canDraw ? "#2ecc71" : "#95a5a6",
            cursor: canDraw ? "pointer" : "not-allowed",
          }}
          title={canDraw ? "Run the draw" : "Fix validation errors first"}
        >
          Draw
        </button>
      </div>

      {drawnMain && (
        <div style={{ marginTop: 18 }}>
          <h3>Last Draw — {config.name}</h3>
          <div>Main: {drawnMain.join(", ")}</div>
          <div>
            {config.bonusName}: {drawnBonus}
          </div>
        </div>
      )}
    </div>
  );
}

// Minimal inline styles
const styles = {
  container: {
    maxWidth: 900,
    margin: "18px auto",
    padding: 20,
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    color: "#222",
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
  },
  title: { margin: 0, marginBottom: 10 },
  row: { display: "flex", alignItems: "center", gap: 12 },
  label: { fontWeight: 600 },
  select: { padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc" },
  quickPick: {
    marginLeft: 12,
    padding: "6px 10px",
    borderRadius: 6,
    border: "none",
    background: "#3498db",
    color: "#fff",
    cursor: "pointer",
  },
  mainRow: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6 },
  inputBlock: { minWidth: 140 },
  numberInput: {
    width: 120,
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    fontSize: 16,
  },
  bonusInput: {
    width: 120,
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    fontSize: 16,
  },
  fieldError: { color: "#e74c3c", marginTop: 6, fontSize: 13 },
  suggestRow: { marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" },
  suggestBtn: {
    padding: "4px 6px",
    borderRadius: 6,
    border: "1px solid #ddd",
    background: "#f6f6f6",
    cursor: "pointer",
    fontSize: 13,
  },
  drawBtn: {
    padding: "10px 18px",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
  },
};
